import { FC } from 'react';
import { Box, HStack, Spacer } from '@chakra-ui/react';

import { DisputableTransaction } from '__generated__/GQL';
import { Checkbox } from 'components/_core/form';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import Tooltip from 'components/_core/Tooltip';
import TransactionAmount from 'features/Bank/components/TransactionAmount';
import TransactionIcon from 'features/Bank/pages/Overview/Transactions/TransactionIcon';
import { TransactionLabelBuilder } from 'features/Bank/pages/Overview/utils';
import { formatDate, formatRelative } from 'helpers/date';
import { capitalizeFirstLetter } from 'helpers/string';

import paymentLocales from '../../../locales';
import locales from './locales';

type DisputableTransactionRowProps = {
  transaction: DisputableTransaction;
  isSelected: boolean;
  onChange: (transactionId: string) => void;
  isBoxed?: boolean;
};

const DisputableTransactionRow: FC<DisputableTransactionRowProps> = ({
  isBoxed = true,
  isSelected,
  onChange,
  transaction,
}) => {
  const {
    alreadyDisputedAt,
    isAlreadyDisputed,
    paymentMethod,
    transactionAt,
    transactionId,
  } = transaction;

  const label = TransactionLabelBuilder.fromTransaction(transaction).build();

  return (
    <SunshineCard alignItems="center" isBoxed={isBoxed} padding="space-16">
      <SunshineCard.Slot name="aside">
        <Tooltip
          description={
            isAlreadyDisputed && alreadyDisputedAt
              ? locales.formatString(locales.alreadyDisputed, {
                  date: formatDate(new Date(alreadyDisputedAt), 'PPatp'),
                })
              : null
          }
          placement="bottom"
        >
          <Box>
            <Checkbox
              isChecked={isSelected}
              isDisabled={isAlreadyDisputed}
              onChange={() => onChange(transactionId)}
              width="space-24"
            />
          </Box>
        </Tooltip>
      </SunshineCard.Slot>

      <SunshineCard.Content
        alignItems="center"
        display="flex"
        flexDirection="row"
      >
        <TransactionIcon size="md" transaction={transaction} />
        <Box marginRight="space-12" />

        <Box>
          <Text noOfLines={1} variant="primary">
            {label}
          </Text>

          <HStack alignItems="flex-start">
            <Text size="sm" variant="light">
              {capitalizeFirstLetter(
                formatRelative(new Date(transactionAt), new Date()),
              )}
            </Text>
          </HStack>
        </Box>
        <Spacer />

        <Text textAlign="right" variant="light">
          {paymentLocales[paymentMethod]}
        </Text>
        <Box width="space-48" />

        <TransactionAmount transaction={transaction} />
      </SunshineCard.Content>
    </SunshineCard>
  );
};

export default DisputableTransactionRow;
